import { Organisations } from '../../shared/collections/organisations';
import { Customer } from '../../shared/models/customer';
import {
  CompleteDepartmentPermissions,
  DepartmentPermissions,
} from '../../shared/models/dispoPermissions';

export function getReadDepartmentIds(
  permissions: DepartmentPermissions,
): string[] {
  if (!permissions.specificDepartmentIds) {
    return [];
  }
  return Object.entries(permissions.specificDepartmentIds)
    .filter(([_, department]) => department.read)
    .map(([id]) => id);
}

export function getWriteDepartmentIds(
  permissions: DepartmentPermissions,
): string[] {
  if (!permissions.specificDepartmentIds) {
    return [];
  }
  return Object.keys(permissions.specificDepartmentIds).filter(
    (departmentId) =>
      permissions.specificDepartmentIds &&
      permissions.specificDepartmentIds[departmentId].write,
  );
}

export function validateDepartmentWriteAccess(
  creatorPermissions: CompleteDepartmentPermissions,
  targetDepartmentPermissions: DepartmentPermissions,
): boolean {
  if (creatorPermissions.writeAll) {
    return true;
  }

  if (
    targetDepartmentPermissions.writeAll ||
    targetDepartmentPermissions.readAll
  ) {
    return false;
  }

  const creatorWriteDepartments = getWriteDepartmentIds(creatorPermissions);

  const targetReadDepartments = getReadDepartmentIds(
    targetDepartmentPermissions,
  );
  const targetWriteDepartments = getWriteDepartmentIds(
    targetDepartmentPermissions,
  );

  const allTargetDepartments = [
    ...new Set([...targetReadDepartments, ...targetWriteDepartments]),
  ];

  return allTargetDepartments.every((departmentId) =>
    creatorWriteDepartments.includes(departmentId),
  );
}

export function getDepartmentPermissionJobQuery(
  permissions: CompleteDepartmentPermissions,
  access: 'read' | 'write' = 'read',
): { 'department.id'?: { $in: string[] } } {
  if (access === 'read') {
    if (permissions.readAll) {
      return {};
    }
    return {
      'department.id': { $in: getReadDepartmentIds(permissions) },
    };
  } else {
    if (permissions.writeAll) {
      return {};
    }
    return {
      'department.id': { $in: getWriteDepartmentIds(permissions) },
    };
  }
}

export function getDepartmentPermissionEmploymentQuery(
  permissions: CompleteDepartmentPermissions,
  access: 'read' | 'write' = 'read',
): any {
  if (access === 'read') {
    if (permissions.readAll) {
      return {};
    }
    return {
      'department.id': { $in: getReadDepartmentIds(permissions) },
    };
  } else {
    if (permissions.writeAll) {
      return {};
    }
    return {
      'department.id': { $in: getWriteDepartmentIds(permissions) },
    };
  }
}

export function getCustomerDepartmentIds(customer: Customer): string[] {
  let organisations = Organisations.find({
    _id: { $in: customer.organisationIds },
  }).fetch();
  if (!organisations?.length) {
    return [];
  }
  return organisations
    .map((organisation) => organisation.departments)
    .flat()
    .map((department) => department.id);
}
